import React, { createContext, useContext, useCallback, useState, ReactNode } from 'react';
import type { League, LeagueFormat, LeagueTiebreaker, TeamStanding, ArchivedSeason, NotableBreak, WinLeaderEntry, FixtureStatus } from '@shootout-comps/types';
import { useAuth } from '../auth/AuthContext';

export interface LeagueAdmins {
  owner: { _id: string; username: string };
  admins: { _id: string; username: string }[];
}

export interface TeamAdmins {
  admins: { _id: string; username: string }[];
}

// Shape returned by GET /public-leagues — a lighter projection than the full League (no
// rosters), plus the host's username since the viewer isn't the owner. Mirrors
// TournamentContext's PublicTournamentSummary.
export interface PublicLeagueSummary {
  _id: string;
  name: string;
  status: string;
  ownerId: string;
  ownerUsername: string | null;
  divisions: { _id: string; name: string; format: LeagueFormat; teams: { _id: string; name: string }[] }[];
  createdAt: string;
  updatedAt: string;
}

export interface DivisionInput {
  name: string;
  format: LeagueFormat;
  framesPerFixture?: number;
  maxHandicapDifference?: number | null;
  pointsPerFrameWon?: number;
  pointsPerMatchWin?: number;
  pointsPerMatchDraw?: number;
  pointsPerMatchLoss?: number;
  tiebreakers?: LeagueTiebreaker[];
  tier?: number;
  promotionSpots?: number;
  relegationSpots?: number;
}

export interface ProposedMove {
  teamId: string; teamName: string;
  direction: 'promote' | 'relegate';
  toDivisionId: string; toDivisionName: string;
}

export interface EndSeasonPreviewDivision {
  divisionId: string; divisionName: string;
  standings: TeamStanding[];
  promotionSpots: number; relegationSpots: number;
  proposedMoves: ProposedMove[];
}

export interface EndSeasonPreview {
  divisions: EndSeasonPreviewDivision[];
}

export interface ArchivedSeasonSummary {
  _id: string; seasonLabel: string; endedAt: string;
  promotionSpots: number; relegationSpots: number;
}

export interface RosterPlayerHistoryEntry {
  fixtureId: string; frameMatchId: string;
  opponentName: string; opponentTeamName: string; isHome: boolean;
  scoreFor: number | null; scoreAgainst: number | null; won: boolean | null;
  highBreak: number;
  scheduledAt: string | null; completedAt: string | null;
}

export interface RosterPlayerUpcomingFixture {
  fixtureId: string; opponentTeamName: string; isHome: boolean;
  scheduledAt: string | null; venue: string | null; status: FixtureStatus;
}

export interface RosterPlayerHistory {
  player: { _id: string; name: string; handicap: number };
  team: { _id: string; name: string };
  matches: RosterPlayerHistoryEntry[];
  upcomingFixtures: RosterPlayerUpcomingFixture[];
}

interface LeagueContextValue {
  loading: boolean;
  error: string | null;

  fetchLeagues: () => Promise<League[]>;
  createLeague: (name: string) => Promise<League>;
  fetchLeague: (id: string) => Promise<League>;
  renameLeague: (id: string, name: string) => Promise<League>;
  updateLeagueSettings: (id: string, settings: { isPublic?: boolean; requireClaimApproval?: boolean; notableBreakThreshold?: number | null }) => Promise<League>;
  deleteLeague: (id: string) => Promise<void>;
  fetchNotableBreaks: (leagueId: string) => Promise<NotableBreak[]>;

  fetchPublicLeagues: () => Promise<PublicLeagueSummary[]>;
  fetchPublicLeague: (id: string) => Promise<League>;
  fetchPublicLeagueStandings: (leagueId: string, divId: string) => Promise<TeamStanding[]>;
  fetchPublicSeasons: (leagueId: string, divId: string) => Promise<ArchivedSeasonSummary[]>;
  fetchPublicSeason: (leagueId: string, divId: string, seasonId: string) => Promise<ArchivedSeason>;
  fetchPublicRosterPlayerHistory: (leagueId: string, divId: string, teamId: string, playerId: string) => Promise<RosterPlayerHistory>;
  fetchPublicWinLeaders: (leagueId: string, divId: string) => Promise<WinLeaderEntry[]>;
  fetchPublicBreakLeaders: (leagueId: string, divId: string) => Promise<NotableBreak[]>;

  addDivision: (leagueId: string, input: DivisionInput) => Promise<League>;
  updateDivision: (leagueId: string, divId: string, input: Partial<DivisionInput>) => Promise<League>;
  deleteDivision: (leagueId: string, divId: string) => Promise<League>;

  addTeam: (leagueId: string, divId: string, name: string) => Promise<League>;
  updateTeam: (leagueId: string, divId: string, teamId: string, input: { name?: string; captainPlayerId?: string | null }) => Promise<League>;
  deleteTeam: (leagueId: string, divId: string, teamId: string) => Promise<League>;

  addRosterPlayer: (leagueId: string, divId: string, teamId: string, input: { name: string; handicap?: number }) => Promise<League>;
  updateRosterPlayer: (leagueId: string, divId: string, teamId: string, playerId: string, input: { name?: string; handicap?: number }) => Promise<League>;
  removeRosterPlayer: (leagueId: string, divId: string, teamId: string, playerId: string) => Promise<League>;
  moveRosterPlayer: (leagueId: string, divId: string, teamId: string, playerId: string, toTeamId: string) => Promise<League>;
  fetchRosterPlayerHistory: (leagueId: string, divId: string, teamId: string, playerId: string) => Promise<RosterPlayerHistory>;

  linkRosterUser: (leagueId: string, divId: string, teamId: string, playerId: string, username: string) => Promise<League>;
  cancelPendingLink: (leagueId: string, divId: string, teamId: string, playerId: string) => Promise<League>;
  claimRosterPlayer: (leagueId: string, divId: string, teamId: string, playerId: string) => Promise<League>;
  approveRosterClaim: (leagueId: string, divId: string, teamId: string, playerId: string) => Promise<League>;
  rejectRosterClaim: (leagueId: string, divId: string, teamId: string, playerId: string) => Promise<League>;
  unlinkRosterUser: (leagueId: string, divId: string, teamId: string, playerId: string) => Promise<League>;
  linkRosterStandalonePlayer: (leagueId: string, divId: string, teamId: string, playerId: string, standalonePlayerId: string) => Promise<League>;
  unlinkRosterStandalonePlayer: (leagueId: string, divId: string, teamId: string, playerId: string) => Promise<League>;

  generateFixtures: (leagueId: string, divId: string, doubleRoundRobin?: boolean) => Promise<League>;
  addFixture: (leagueId: string, divId: string, input: { homeTeamId: string; awayTeamId: string; scheduledAt?: string | null; venue?: string | null }) => Promise<League>;
  updateFixture: (leagueId: string, divId: string, fixtureId: string, input: { scheduledAt?: string | null; venue?: string | null }) => Promise<League>;
  setLineup: (leagueId: string, divId: string, fixtureId: string, pairs: { homePlayerId: string; awayPlayerId: string; excludeHomeFromSeasonStats?: boolean; excludeAwayFromSeasonStats?: boolean }[]) => Promise<League>;
  setFixtureResult: (leagueId: string, divId: string, fixtureId: string, input: { homeFrameWins: number; awayFrameWins: number }) => Promise<League>;
  deleteFixture: (leagueId: string, divId: string, fixtureId: string) => Promise<League>;
  // Toggles whether one side's result in an already-created match counts towards the season's
  // win/high-break leaderboards — for a team playing a player twice in one fixture; only that
  // side's own stats are excluded, not their opponent's. Unlike setLineup, this stays callable
  // regardless of whether the match has started or finished.
  setMatchExcludeFromStats: (leagueId: string, divId: string, fixtureId: string, matchId: string, side: 'home' | 'away', exclude: boolean) => Promise<League>;
  // Quick per-match score entry — an alternative to opening full shot-by-shot live scoring.
  // Starts the match first if it hasn't been (a fresh lineup slot has no liveState yet,
  // and /matches/:id/end requires one), then finishes it with the given score/high breaks in one
  // go; the winner is derived server-side from whichever score is higher.
  quickScoreMatch: (matchId: string, alreadyStarted: boolean, input: { score1: number; score2: number; highBreak1?: number; highBreak2?: number }) => Promise<void>;

  fetchStandings: (leagueId: string, divId: string) => Promise<TeamStanding[]>;
  fetchWinLeaders: (leagueId: string, divId: string) => Promise<WinLeaderEntry[]>;
  fetchBreakLeaders: (leagueId: string, divId: string) => Promise<NotableBreak[]>;

  previewEndSeason: (leagueId: string, overrides?: { divisionId: string; promotionSpots?: number; relegationSpots?: number }[]) => Promise<EndSeasonPreview>;
  commitEndSeason: (leagueId: string, seasonLabel: string, divisions: { divisionId: string; promotionSpots: number; relegationSpots: number; applyMoves: { teamId: string; toDivisionId: string }[] }[]) => Promise<League>;
  fetchSeasons: (leagueId: string, divId: string) => Promise<ArchivedSeasonSummary[]>;
  fetchSeason: (leagueId: string, divId: string, seasonId: string) => Promise<ArchivedSeason>;

  fetchLeagueAdmins: (leagueId: string) => Promise<LeagueAdmins>;
  addLeagueAdmin: (leagueId: string, username: string) => Promise<LeagueAdmins>;
  removeLeagueAdmin: (leagueId: string, userId: string) => Promise<LeagueAdmins>;
  fetchTeamAdmins: (leagueId: string, divId: string, teamId: string) => Promise<TeamAdmins>;
  addTeamAdmin: (leagueId: string, divId: string, teamId: string, username: string) => Promise<TeamAdmins>;
  removeTeamAdmin: (leagueId: string, divId: string, teamId: string, userId: string) => Promise<TeamAdmins>;

  // Broadcasts a push notification to every user linked to a roster player in the league (or,
  // if divisionId is given, just that division) who has a mobile device registered.
  sendLeagueNotification: (leagueId: string, input: { title: string; body: string; divisionId?: string }) => Promise<League>;
}

const LeagueContext = createContext<LeagueContextValue | null>(null);

export interface LeagueProviderProps {
  children: ReactNode;
  // Base URL to prefix onto '/api' — '' for web (same-origin relative requests),
  // an absolute origin for mobile. Same convention as TournamentProvider.
  apiBaseUrl: string;
}

export function LeagueProvider({ children, apiBaseUrl }: LeagueProviderProps) {
  const API = `${apiBaseUrl}/api`;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken } = useAuth();

  const jsonRequest = useCallback(async <T,>(path: string, options: RequestInit = {}): Promise<T> => {
    setLoading(true); setError(null);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${API}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(options.headers as Record<string, string> | undefined),
        },
      });
      const data = await res.json() as T & { error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Request failed');
      return data;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      setError(msg); throw e;
    } finally {
      setLoading(false);
    }
  }, [API, getAccessToken]);

  const fetchLeagues = useCallback(() => jsonRequest<League[]>('/leagues'), [jsonRequest]);
  const createLeague = useCallback((name: string) => jsonRequest<League>('/leagues', { method: 'POST', body: JSON.stringify({ name }) }), [jsonRequest]);
  const fetchLeague = useCallback((id: string) => jsonRequest<League>(`/leagues/${id}`), [jsonRequest]);
  const renameLeague = useCallback((id: string, name: string) => jsonRequest<League>(`/leagues/${id}`, { method: 'PUT', body: JSON.stringify({ name }) }), [jsonRequest]);
  const updateLeagueSettings = useCallback((id: string, settings: { isPublic?: boolean; requireClaimApproval?: boolean; notableBreakThreshold?: number | null }) =>
    jsonRequest<League>(`/leagues/${id}`, { method: 'PUT', body: JSON.stringify(settings) }), [jsonRequest]);
  const deleteLeague = useCallback(async (id: string) => { await jsonRequest(`/leagues/${id}`, { method: 'DELETE' }); }, [jsonRequest]);
  const fetchNotableBreaks = useCallback((leagueId: string) =>
    jsonRequest<NotableBreak[]>(`/leagues/${leagueId}/notable-breaks`), [jsonRequest]);

  const fetchPublicLeagues = useCallback(() => jsonRequest<PublicLeagueSummary[]>('/public-leagues'), [jsonRequest]);
  const fetchPublicLeague = useCallback((id: string) => jsonRequest<League>(`/public-leagues/${id}`), [jsonRequest]);
  const fetchPublicLeagueStandings = useCallback((leagueId: string, divId: string) =>
    jsonRequest<TeamStanding[]>(`/public-leagues/${leagueId}/divisions/${divId}/standings`), [jsonRequest]);
  const fetchPublicSeasons = useCallback((leagueId: string, divId: string) =>
    jsonRequest<ArchivedSeasonSummary[]>(`/public-leagues/${leagueId}/divisions/${divId}/seasons`), [jsonRequest]);
  const fetchPublicSeason = useCallback((leagueId: string, divId: string, seasonId: string) =>
    jsonRequest<ArchivedSeason>(`/public-leagues/${leagueId}/divisions/${divId}/seasons/${seasonId}`), [jsonRequest]);
  const fetchPublicRosterPlayerHistory = useCallback((leagueId: string, divId: string, teamId: string, playerId: string) =>
    jsonRequest<RosterPlayerHistory>(`/public-leagues/${leagueId}/divisions/${divId}/teams/${teamId}/roster/${playerId}/history`), [jsonRequest]);
  const fetchPublicWinLeaders = useCallback((leagueId: string, divId: string) =>
    jsonRequest<WinLeaderEntry[]>(`/public-leagues/${leagueId}/divisions/${divId}/leaders/wins`), [jsonRequest]);
  const fetchPublicBreakLeaders = useCallback((leagueId: string, divId: string) =>
    jsonRequest<NotableBreak[]>(`/public-leagues/${leagueId}/divisions/${divId}/leaders/breaks`), [jsonRequest]);

  const addDivision = useCallback((leagueId: string, input: DivisionInput) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions`, { method: 'POST', body: JSON.stringify(input) }), [jsonRequest]);
  const updateDivision = useCallback((leagueId: string, divId: string, input: Partial<DivisionInput>) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}`, { method: 'PUT', body: JSON.stringify(input) }), [jsonRequest]);
  const deleteDivision = useCallback((leagueId: string, divId: string) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}`, { method: 'DELETE' }), [jsonRequest]);

  const addTeam = useCallback((leagueId: string, divId: string, name: string) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams`, { method: 'POST', body: JSON.stringify({ name }) }), [jsonRequest]);
  const updateTeam = useCallback((leagueId: string, divId: string, teamId: string, input: { name?: string; captainPlayerId?: string | null }) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}`, { method: 'PUT', body: JSON.stringify(input) }), [jsonRequest]);
  const deleteTeam = useCallback((leagueId: string, divId: string, teamId: string) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}`, { method: 'DELETE' }), [jsonRequest]);

  const addRosterPlayer = useCallback((leagueId: string, divId: string, teamId: string, input: { name: string; handicap?: number }) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/roster`, { method: 'POST', body: JSON.stringify(input) }), [jsonRequest]);
  const updateRosterPlayer = useCallback((leagueId: string, divId: string, teamId: string, playerId: string, input: { name?: string; handicap?: number }) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/roster/${playerId}`, { method: 'PUT', body: JSON.stringify(input) }), [jsonRequest]);
  const removeRosterPlayer = useCallback((leagueId: string, divId: string, teamId: string, playerId: string) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/roster/${playerId}`, { method: 'DELETE' }), [jsonRequest]);
  const moveRosterPlayer = useCallback((leagueId: string, divId: string, teamId: string, playerId: string, toTeamId: string) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/roster/${playerId}/move`, { method: 'POST', body: JSON.stringify({ toTeamId }) }), [jsonRequest]);
  const fetchRosterPlayerHistory = useCallback((leagueId: string, divId: string, teamId: string, playerId: string) =>
    jsonRequest<RosterPlayerHistory>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/roster/${playerId}/history`), [jsonRequest]);

  const linkRosterUser = useCallback((leagueId: string, divId: string, teamId: string, playerId: string, username: string) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/roster/${playerId}/link-user`, { method: 'PUT', body: JSON.stringify({ username }) }), [jsonRequest]);
  const cancelPendingLink = useCallback((leagueId: string, divId: string, teamId: string, playerId: string) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/roster/${playerId}/pending-link`, { method: 'DELETE' }), [jsonRequest]);
  const claimRosterPlayer = useCallback((leagueId: string, divId: string, teamId: string, playerId: string) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/roster/${playerId}/claim`, { method: 'POST' }), [jsonRequest]);
  const approveRosterClaim = useCallback((leagueId: string, divId: string, teamId: string, playerId: string) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/roster/${playerId}/approve-claim`, { method: 'POST' }), [jsonRequest]);
  const rejectRosterClaim = useCallback((leagueId: string, divId: string, teamId: string, playerId: string) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/roster/${playerId}/pending-claim`, { method: 'DELETE' }), [jsonRequest]);
  const unlinkRosterUser = useCallback((leagueId: string, divId: string, teamId: string, playerId: string) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/roster/${playerId}/link-user`, { method: 'DELETE' }), [jsonRequest]);
  const linkRosterStandalonePlayer = useCallback((leagueId: string, divId: string, teamId: string, playerId: string, standalonePlayerId: string) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/roster/${playerId}/link-player`, { method: 'PUT', body: JSON.stringify({ playerId: standalonePlayerId }) }), [jsonRequest]);
  const unlinkRosterStandalonePlayer = useCallback((leagueId: string, divId: string, teamId: string, playerId: string) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/roster/${playerId}/link-player`, { method: 'DELETE' }), [jsonRequest]);

  const generateFixtures = useCallback((leagueId: string, divId: string, doubleRoundRobin?: boolean) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/generate-fixtures`, { method: 'POST', body: JSON.stringify({ doubleRoundRobin }) }), [jsonRequest]);
  const addFixture = useCallback((leagueId: string, divId: string, input: { homeTeamId: string; awayTeamId: string; scheduledAt?: string | null; venue?: string | null }) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/fixtures`, { method: 'POST', body: JSON.stringify(input) }), [jsonRequest]);
  const updateFixture = useCallback((leagueId: string, divId: string, fixtureId: string, input: { scheduledAt?: string | null; venue?: string | null }) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/fixtures/${fixtureId}`, { method: 'PUT', body: JSON.stringify(input) }), [jsonRequest]);
  const setLineup = useCallback((leagueId: string, divId: string, fixtureId: string, pairs: { homePlayerId: string; awayPlayerId: string; excludeHomeFromSeasonStats?: boolean; excludeAwayFromSeasonStats?: boolean }[]) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/fixtures/${fixtureId}/lineup`, { method: 'POST', body: JSON.stringify({ pairs }) }), [jsonRequest]);
  const setMatchExcludeFromStats = useCallback((leagueId: string, divId: string, fixtureId: string, matchId: string, side: 'home' | 'away', exclude: boolean) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/fixtures/${fixtureId}/matches/${matchId}/exclude-from-stats`, { method: 'PUT', body: JSON.stringify({ side, exclude }) }), [jsonRequest]);
  const setFixtureResult = useCallback((leagueId: string, divId: string, fixtureId: string, input: { homeFrameWins: number; awayFrameWins: number }) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/fixtures/${fixtureId}/result`, { method: 'PUT', body: JSON.stringify(input) }), [jsonRequest]);
  const deleteFixture = useCallback((leagueId: string, divId: string, fixtureId: string) =>
    jsonRequest<League>(`/leagues/${leagueId}/divisions/${divId}/fixtures/${fixtureId}`, { method: 'DELETE' }), [jsonRequest]);
  const quickScoreMatch = useCallback(async (matchId: string, alreadyStarted: boolean, input: { score1: number; score2: number; highBreak1?: number; highBreak2?: number }) => {
    if (!alreadyStarted) await jsonRequest(`/matches/${matchId}/start`, { method: 'POST' });
    await jsonRequest(`/matches/${matchId}/end`, { method: 'POST', body: JSON.stringify(input) });
  }, [jsonRequest]);

  const fetchStandings = useCallback((leagueId: string, divId: string) =>
    jsonRequest<TeamStanding[]>(`/leagues/${leagueId}/divisions/${divId}/standings`), [jsonRequest]);
  const fetchWinLeaders = useCallback((leagueId: string, divId: string) =>
    jsonRequest<WinLeaderEntry[]>(`/leagues/${leagueId}/divisions/${divId}/leaders/wins`), [jsonRequest]);
  const fetchBreakLeaders = useCallback((leagueId: string, divId: string) =>
    jsonRequest<NotableBreak[]>(`/leagues/${leagueId}/divisions/${divId}/leaders/breaks`), [jsonRequest]);

  const previewEndSeason = useCallback((leagueId: string, overrides?: { divisionId: string; promotionSpots?: number; relegationSpots?: number }[]) =>
    jsonRequest<EndSeasonPreview>(`/leagues/${leagueId}/end-season/preview`, { method: 'POST', body: JSON.stringify({ divisions: overrides }) }), [jsonRequest]);
  const commitEndSeason = useCallback((leagueId: string, seasonLabel: string, divisions: { divisionId: string; promotionSpots: number; relegationSpots: number; applyMoves: { teamId: string; toDivisionId: string }[] }[]) =>
    jsonRequest<League>(`/leagues/${leagueId}/end-season/commit`, { method: 'POST', body: JSON.stringify({ seasonLabel, divisions }) }), [jsonRequest]);
  const fetchSeasons = useCallback((leagueId: string, divId: string) =>
    jsonRequest<ArchivedSeasonSummary[]>(`/leagues/${leagueId}/divisions/${divId}/seasons`), [jsonRequest]);
  const fetchSeason = useCallback((leagueId: string, divId: string, seasonId: string) =>
    jsonRequest<ArchivedSeason>(`/leagues/${leagueId}/divisions/${divId}/seasons/${seasonId}`), [jsonRequest]);

  const fetchLeagueAdmins = useCallback((leagueId: string) => jsonRequest<LeagueAdmins>(`/leagues/${leagueId}/admins`), [jsonRequest]);
  const addLeagueAdmin = useCallback((leagueId: string, username: string) =>
    jsonRequest<LeagueAdmins>(`/leagues/${leagueId}/admins`, { method: 'POST', body: JSON.stringify({ username }) }), [jsonRequest]);
  const removeLeagueAdmin = useCallback((leagueId: string, userId: string) =>
    jsonRequest<LeagueAdmins>(`/leagues/${leagueId}/admins/${userId}`, { method: 'DELETE' }), [jsonRequest]);
  const fetchTeamAdmins = useCallback((leagueId: string, divId: string, teamId: string) =>
    jsonRequest<TeamAdmins>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/admins`), [jsonRequest]);
  const addTeamAdmin = useCallback((leagueId: string, divId: string, teamId: string, username: string) =>
    jsonRequest<TeamAdmins>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/admins`, { method: 'POST', body: JSON.stringify({ username }) }), [jsonRequest]);
  const removeTeamAdmin = useCallback((leagueId: string, divId: string, teamId: string, userId: string) =>
    jsonRequest<TeamAdmins>(`/leagues/${leagueId}/divisions/${divId}/teams/${teamId}/admins/${userId}`, { method: 'DELETE' }), [jsonRequest]);

  const sendLeagueNotification = useCallback((leagueId: string, input: { title: string; body: string; divisionId?: string }) =>
    jsonRequest<League>(`/leagues/${leagueId}/notify`, { method: 'POST', body: JSON.stringify(input) }), [jsonRequest]);

  return (
    <LeagueContext.Provider value={{
      loading, error,
      fetchLeagues, createLeague, fetchLeague, renameLeague, updateLeagueSettings, deleteLeague, fetchNotableBreaks,
      fetchPublicLeagues, fetchPublicLeague, fetchPublicLeagueStandings, fetchPublicSeasons, fetchPublicSeason, fetchPublicRosterPlayerHistory, fetchPublicWinLeaders, fetchPublicBreakLeaders,
      addDivision, updateDivision, deleteDivision,
      addTeam, updateTeam, deleteTeam,
      addRosterPlayer, updateRosterPlayer, removeRosterPlayer, moveRosterPlayer, fetchRosterPlayerHistory,
      linkRosterUser, cancelPendingLink, claimRosterPlayer, approveRosterClaim, rejectRosterClaim, unlinkRosterUser, linkRosterStandalonePlayer, unlinkRosterStandalonePlayer,
      generateFixtures, addFixture, updateFixture, setLineup, setMatchExcludeFromStats, setFixtureResult, deleteFixture, quickScoreMatch,
      fetchStandings, fetchWinLeaders, fetchBreakLeaders,
      previewEndSeason, commitEndSeason, fetchSeasons, fetchSeason,
      fetchLeagueAdmins, addLeagueAdmin, removeLeagueAdmin, fetchTeamAdmins, addTeamAdmin, removeTeamAdmin, sendLeagueNotification,
    }}>
      {children}
    </LeagueContext.Provider>
  );
}

export function useLeague(): LeagueContextValue {
  const ctx = useContext(LeagueContext);
  if (!ctx) throw new Error('useLeague must be used within a LeagueProvider');
  return ctx;
}
